import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DiscoverFarmProfile } from "./DiscoverFarmProfile";
import { DiscoverFarmList } from "./discover/DiscoverFarmList";
import { DiscoverFilters } from "./discover/DiscoverFilters";
import { GrowingZoneFilter } from "./GrowingZoneFilter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Filter, Users } from "lucide-react";
import { useFarmProfiles } from "@/hooks/useFarmProfiles";
import { useCommunities } from "@/hooks/useCommunities";
import { useLiteMode } from "@/hooks/useLiteMode";
import { transformProfilesToFarms } from "@/utils/farmProfileTransform";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorScreen } from "./ErrorScreen";
import { getCommunityById } from "@/data/communities";
import { useIsMobile } from "@/hooks/use-mobile";
import { DiscoverTeams } from "./discover/DiscoverTeams";

interface Location {
  lat: number;
  lng: number;
  address: string;
}

interface Farm {
  id: number;
  name: string;
  username: string;
  avatar: string;
  cover: string;
  location: Location;
  crops: string[];
  distance: string;
  description: string;
  verified: boolean;
  category: string;
  calculatedDistance: number;
  plantHardinessZone: string;
  userId?: string;
}

export const Discover = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') || 'farms';
  const [selectedView, setSelectedView] = useState<'farms' | 'teams'>(
    viewParam === 'teams' ? 'teams' : 'farms'
  );
  
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [searchRadius, setSearchRadius] = useState("25");
  const [sortBy, setSortBy] = useState("distance");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [zoneFilters, setZoneFilters] = useState<{
    showMyZoneOnly: boolean;
  }>({
    showMyZoneOnly: false,
  });
  
  const isMobile = useIsMobile();
  
  // Update URL when view changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedView === 'teams') {
      newParams.set('view', 'teams');
    } else {
      newParams.delete('view');
    }
    setSearchParams(newParams, { replace: true });
  }, [selectedView]);
  
  // Fetch real farm profiles from database
  const { farms: farmProfiles, loading, error } = useFarmProfiles();
  const { isLiteMode } = useLiteMode();
  const { communities, loading: communitiesLoading } = useCommunities();

  const handleVisitFarm = (farm: Farm) => {
    setSelectedFarm(farm);
  };

  const handleBackToDiscover = () => {
    setSelectedFarm(null);
  };

  // Transform profiles to farms and apply filters
  const transformedFarms = transformProfilesToFarms(farmProfiles);
  
  const filteredFarms = transformedFarms.filter(farm => {
    // Community filter
    if (selectedCommunity !== "all" && selectedCommunity !== "see-all") {
      const community = getCommunityById(selectedCommunity);
      if (community && !farm.category.toLowerCase().includes(community.name.toLowerCase()) && 
          !farm.crops.some(crop => crop.toLowerCase().includes(community.name.toLowerCase()))) {
        return false;
      }
    }
    
    // Location/Distance filter
    if (locationFilter !== "all") {
      const maxDistance = locationFilter === "25-miles" ? 25 : 
                         locationFilter === "50-miles" ? 50 : 
                         locationFilter === "100-miles" ? 100 : Infinity;
      if (farm.calculatedDistance > maxDistance) {
        return false;
      }
    }
    
    return true;
  });
  
  const sortedFarms = [...filteredFarms].sort((a, b) => {
    if (sortBy === "distance") {
      return a.calculatedDistance - b.calculatedDistance;
    }
    return a.name.localeCompare(b.name);
  });

  // Handle loading state
  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="p-4">
          <LoadingScreen />
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen">
        <div className="p-4">
          <ErrorScreen 
            authError={error} 
            onRetry={() => window.location.reload()} 
          />
        </div>
      </div>
    );
  }

  if (selectedFarm) {
    return (
      <DiscoverFarmProfile 
        farm={selectedFarm} 
        onBack={handleBackToDiscover} 
      />
    );
  }

  return (
    <div className="h-full">
      <div className="p-4 space-y-6">
        {/* Header with Selector */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">
              {selectedView === 'farms' ? 'Discover Farms' : 'Discover tAG Teams'}
            </h1>
            {!isMobile && selectedView === 'farms' && (
              <Button 
                variant="outline" 
                size="sm"
                className={showFilters ? 'bg-primary text-primary-foreground' : ''}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filter by Community
              </Button>
            )}
          </div>
          
          {/* View Selector */}
          <Tabs value={selectedView} onValueChange={(v) => setSelectedView(v as 'farms' | 'teams')}>
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="farms">
                <MapPin className="w-4 h-4 mr-2" />
                Farms
              </TabsTrigger>
              <TabsTrigger value="teams">
                <Users className="w-4 h-4 mr-2" />
                tAG Teams
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Farms View */}
        {selectedView === 'farms' && (
          <>
            {/* Ultra Compact Filter Controls */}
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center text-green-600">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">Evans, GA</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <select
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5 h-6"
                  >
                    <option value="10">10mi</option>
                    <option value="25">25mi</option>
                    <option value="50">50mi</option>
                    <option value="100">100mi</option>
                  </select>
                  
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5 h-6"
                  >
                    <option value="distance">Distance</option>
                    <option value="name">Name</option>
                  </select>
                  
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">Same Climate Zone</span>
                    <GrowingZoneFilter
                      onFilterChange={(filters) => {
                        console.log('Zone filter changed:', filters);
                        setZoneFilters(filters);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <DiscoverFilters 
              selectedCommunity={selectedCommunity}
              onCommunityChange={setSelectedCommunity}
              locationFilter={locationFilter}
              onLocationFilterChange={setLocationFilter}
              communities={communities}
              communitiesLoading={communitiesLoading}
              filteredCount={sortedFarms.length}
              totalCount={transformedFarms.length}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
            />

            {/* Farm List */}
            {sortedFarms.length === 0 ? (
              <div className="bg-white rounded-lg p-8 shadow-sm text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Farms Found</h3>
                <p className="text-gray-600">
                  No farm profiles have been created yet. Create your farm profile to appear in the discovery list!
                </p>
              </div>
            ) : (
              <DiscoverFarmList 
                farms={isLiteMode ? sortedFarms.slice(0, 10) : sortedFarms.slice(0, 50)}
                onFarmClick={handleVisitFarm}
                isLiteMode={isLiteMode}
              />
            )}
          </>
        )}
        
        {/* Teams View */}
        {selectedView === 'teams' && (
          <DiscoverTeams />
        )}
      </div>
    </div>
  );
};
